import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { fileImageSrcSet } from '../../lib/file-image';
import { cn } from '../../lib/utils';
import { useReducedMotion } from '../../hooks/use-reduced-motion';
import { ProgressDots } from '../progress-dots';

export type CarouselImage = {
  src: string;
  /**
   * What the picture shows. Omit it only when the image is decorative — the
   * slide then renders `alt=""`, which is the correct markup for "skip this",
   * and is why there is no invented fallback here. A made-up description is
   * worse than none: a screen reader reads it as fact.
   */
  alt?: string;
  /**
   * `flat` only — the mono label over the caption, "Local talent". The slide's
   * number is put in front of it for you, so the pair reads "01 · LOCAL TALENT".
   */
  eyebrow?: string;
  /** `flat` only — the line laid over the foot of the picture. */
  caption?: string;
  /**
   * What the slide's dot calls it — "Book" for a dot read as "Show Book".
   * Without one the dot is numbered, "Show image 2 of 3".
   */
  label?: string;
};

export type ImageCarouselProps = {
  /** Plain URLs, or `{ src, alt }` when the pictures carry meaning. */
  images: ReadonlyArray<string | CarouselImage>;
  /**
   * `stack`, the default, is the fan: one picture in front, its neighbours
   * squeezed and tilted behind it, dots underneath.
   *
   * `flat` is one picture at a time with its caption laid over it and a
   * chevron either side — for a band where the picture sits beside text that
   * is already doing the talking, and a fan would compete with it.
   *
   * `fill` is the same crossfade with no frame of its own: it fills whatever
   * box it is put in, edge to edge, with the dots laid over its foot. For a
   * photo that is a card's whole side rather than a picture mounted on it.
   */
  variant?: 'stack' | 'flat' | 'fill';
  /**
   * Names the carousel for anyone who cannot see it — "MDS café hall", not
   * "carousel". Required, because a region with no name is one a screen-reader
   * user meets as "group" and has to enter to identify.
   */
  label: string;
  /** Milliseconds between automatic advances. `0` turns autoplay off. */
  interval?: number;
  /**
   * Makes the carousel controlled: it shows this slide, and every route that
   * would change it (swipe, keys, dots) calls `onIndexChange` instead. Its own
   * timer is off, because the caller is the one keeping time. For a carousel
   * driven by something beside it, such as a list of steps.
   */
  index?: number;
  onIndexChange?: (index: number) => void;
  /**
   * `fill` only. Swaps the dots for `ProgressDots`, the frosted pill whose
   * current bar fills from 0 to 1 with this value. For a controlled carousel
   * whose caller knows how far through a slide it is.
   */
  progress?: number;
  /**
   * `fill` only. `fade`, the default, crossfades. `slide` carries the pictures
   * across the frame left to right: the arriving one in from the left, the
   * leaving one out to the right.
   */
  transition?: 'fade' | 'slide';
  /** `fill` only. A slow push-in on the picture while it is on show. */
  zoom?: boolean;
  /**
   * `fill` only. Drawn over the pictures and under the dots: a caption, a
   * scrim. It lets every pointer through, so a swipe that starts on the words
   * still moves the pictures; anything in it that needs a click must set
   * `pointer-events-auto` for itself.
   */
  children?: React.ReactNode;
  className?: string;
};

/*
 * The geometry below is transcribed from the site design's own `Feature Split`
 * component rather than derived, because every one of these numbers is a
 * judgement someone already made and the arrangement is not robust to guessing
 * at them. Notes are on what each one is doing, not on where it came from.
 */

/**
 * How far a neighbour sits from the centre, as a share of a slide's width.
 *
 * The design's own value is 0.58, and that is deliberately not what is here.
 * At 0.58 a neighbour's outer edge lands about 0.84 of a container width from
 * the middle — a long way past the frame — which works in the design because
 * its gallery sits in a wide column with 110–180px of padding for the overflow
 * to spill into. Dropped into a capped column the same number just shows most
 * of the picture behind.
 *
 * 0.39 puts roughly a fifth of each neighbour past the front picture's edge,
 * which is the amount that reads as "there is another photograph back there"
 * without turning the group into three pictures in a row.
 */
const SPREAD = 0.39;

/**
 * The tilt, in degrees — left picture anticlockwise, right one clockwise.
 *
 * Small on purpose. This is a flat, in-plane `rotate`, not a `rotateY`: turning
 * a picture on its vertical axis sends it into the screen, which at this size
 * reads as a rendering fault rather than as a fan. Six degrees is enough to
 * break the pictures out of a dead-straight row and not enough to look askew.
 */
const TILT = 6;

/**
 * A neighbour's scale — and the whole trick of the arrangement is that it is
 * not uniform. Squeezed hard horizontally and barely at all vertically, so a
 * flanking picture reads as a tall narrow sliver standing behind the front one
 * rather than as a second photograph sitting next to it. Scaling both axes
 * together gives three pictures in a row, which is the wrong picture entirely.
 */
const SCALE_X = 0.62;
const SCALE_Y = 0.88;

/** Blur and dim on a neighbour, so the eye lands on the front picture. */
const BLUR = 4;
const DIM = 0.5;

/**
 * How wide a slide is drawn, for picking a display size: half a split section
 * on a wide screen, the whole width below it.
 */
const CAROUSEL_IMAGE_SIZES = '(min-width: 1024px) 50vw, 100vw';

/** Past this many pixels of drag, letting go moves a slide instead of snapping back. */
const DRAG_THRESHOLD = 56;

function normalise(image: string | CarouselImage): CarouselImage {
  return typeof image === 'string' ? { src: image } : image;
}

/**
 * The shortest signed distance from the active slide to slide `i` on a ring of
 * `count`. Going from the last slide to the first has to read as +1 rather than
 * as −7, or the whole stack would animate the long way round on the wrap.
 */
function offsetFrom(i: number, active: number, count: number) {
  const raw = i - active;
  const half = count / 2;
  if (raw > half) return raw - count;
  if (raw < -half) return raw + count;
  return raw;
}

/**
 * The site's picture gallery: one photograph square-on in front, its two
 * neighbours squeezed into slivers, tilted and dimmed behind it.
 *
 * It advances on a timer, on a swipe, on the arrow keys, on the dots, and on a
 * click on either visible neighbour — and every one of those routes changes the
 * same `index`, so they all produce the same move. Clicking the last dot from
 * the first picture travels; it does not cut.
 *
 * Autoplay stops while a pointer is over it, while focus is inside it, and
 * during a drag, and never starts at all when the visitor has asked their OS
 * for less motion.
 */
export function ImageCarousel({
  images,
  label,
  variant = 'stack',
  interval = 5600,
  index: controlledIndex,
  onIndexChange,
  progress,
  transition = 'fade',
  zoom = false,
  children,
  className,
}: ImageCarouselProps) {
  const slides = React.useMemo(() => images.map(normalise), [images]);
  const count = slides.length;
  const controlled = controlledIndex !== undefined;

  const reducedMotion = useReducedMotion();
  const [active, setActive] = React.useState(0);
  const [engaged, setEngaged] = React.useState(false);
  const [drag, setDrag] = React.useState<{ from: number; dx: number } | null>(null);

  // A shorter list than the one we are sitting on would leave `active` past the
  // end, so it is clamped rather than trusted.
  const index = count > 0 ? Math.min(controlled ? controlledIndex : active, count - 1) : 0;

  // The slide that was on show before this one, which `slide` carries out to
  // the right. Updated during render, React's pattern for state derived from
  // a change, so the leaving slide is known on the same render that the
  // arriving one is. -1 on the first render, where nothing should move.
  const [shown, setShown] = React.useState({ current: index, previous: -1 });
  if (shown.current !== index) setShown({ current: index, previous: shown.current });
  const previous = shown.previous;

  const go = React.useCallback(
    (next: number) => {
      if (count === 0) return;
      const wrapped = ((next % count) + count) % count;
      if (controlled) onIndexChange?.(wrapped);
      else setActive(wrapped);
    },
    [count, controlled, onIndexChange],
  );

  const autoplaying =
    !controlled && count > 1 && interval > 0 && !reducedMotion && !engaged && !drag;

  React.useEffect(() => {
    if (!autoplaying) return;
    const id = window.setInterval(() => setActive((i) => (i + 1) % count), interval);
    return () => window.clearInterval(id);
  }, [autoplaying, count, interval]);

  function onPointerDown(event: React.PointerEvent<HTMLDivElement>) {
    if (count < 2 || event.button !== 0) return;
    // A press that starts on one of the controls is a press, not a drag.
    // Without this the capture below redirects the rest of the gesture to the
    // section, the button never sees a pointerup on itself, and the click it
    // would have fired is simply lost — the arrows and dots would look dead.
    if ((event.target as HTMLElement).closest('[data-carousel-control]')) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    setDrag({ from: event.clientX, dx: 0 });
  }

  function onPointerMove(event: React.PointerEvent<HTMLDivElement>) {
    setDrag((d) => (d ? { ...d, dx: event.clientX - d.from } : d));
  }

  function onPointerUp() {
    if (!drag) return;
    if (drag.dx <= -DRAG_THRESHOLD) go(index + 1);
    else if (drag.dx >= DRAG_THRESHOLD) go(index - 1);
    setDrag(null);
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLElement>) {
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      go(index + 1);
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault();
      go(index - 1);
    }
  }

  // A `fill` with an overlay still draws it: the words over the pictures
  // should not wait for the pictures to arrive.
  if (count === 0 && !(variant === 'fill' && children)) return null;

  // Every route in — timer, swipe, keys, hover pause — is shared by both
  // variants; only what gets drawn differs.
  const handlers = {
    onMouseEnter: () => setEngaged(true),
    onMouseLeave: () => setEngaged(false),
    // React's onFocus/onBlur bubble, so these fire for the controls too —
    // tabbing into them stops the timer moving the picture out from under the
    // person reading it.
    onFocus: () => setEngaged(true),
    onBlur: () => setEngaged(false),
    onKeyDown,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel: onPointerUp,
  };

  // Announcing each change is for a carousel moved by hand. One that moves on a
  // timer (its own, or a controlling caller's) would talk over the page.
  const live = autoplaying || controlled ? 'off' : 'polite';

  const dotLabel = (i: number) => `Show ${slides[i]?.label ?? `image ${i + 1} of ${count}`}`;

  // The row of dots, shared by `stack` and `fill`.
  const dots = count > 1 && (
    <div className="absolute inset-x-0 bottom-3.5 z-20 flex items-center justify-center gap-1">
      {slides.map((_, i) => (
        <button
          key={i}
          type="button"
          data-carousel-control
          onClick={() => go(i)}
          aria-label={dotLabel(i)}
          aria-current={i === index}
          // The dot is 7px tall; the button around it is 24px, which is the
          // target WCAG 2.2 asks for at AA. The padding is transparent, so
          // the row still looks like the design's row of dots.
          className={cn(
            'grid h-6 place-items-center px-0.5',
            'focus-visible:outline-2 focus-visible:outline-offset-2',
            'focus-visible:outline-focus-on-panel',
          )}
        >
          <span
            className={cn(
              'h-1.75 rounded-button transition-all duration-(--duration-base)',
              'ease-standard motion-reduce:transition-none',
              i === index ? 'w-4.5 bg-on-panel' : 'w-2 bg-on-panel/50',
            )}
          />
        </button>
      ))}
    </div>
  );

  if (variant === 'fill') {
    const slide = transition === 'slide';

    return (
      <section
        aria-roledescription="carousel"
        aria-label={label}
        className={cn('relative size-full touch-pan-y overflow-hidden select-none', className)}
        aria-live={live}
        {...handlers}
      >
        {slides.map((image, i) => {
          const isActive = i === index;
          const isLeaving = i === previous && !isActive;
          return (
            // `fade` is a crossfade, as `flat`, for the same reason. `slide`
            // keeps every picture opaque and moves it instead: on show at 0,
            // the one just left going out to the right, and every other one
            // parked off the left edge, waiting to come in. Only the first two
            // are transitioned, so a picture sent back to wait jumps there
            // unseen instead of crossing the frame the wrong way.
            <div
              key={`${image.src}-${i}`}
              aria-hidden={isActive ? undefined : true}
              className={cn(
                'absolute inset-0 overflow-hidden',
                slide
                  ? [
                      isActive ? 'translate-x-0' : isLeaving ? 'translate-x-full' : '-translate-x-full',
                      (isActive || isLeaving) &&
                        'transition-transform duration-(--duration-slow) ease-travel',
                    ]
                  : [
                      'transition-opacity duration-(--duration-slow) ease-standard',
                      isActive ? 'opacity-100' : 'opacity-0',
                    ],
                'motion-reduce:transition-none',
              )}
            >
              <img
                src={image.src}
                srcSet={fileImageSrcSet(image.src)}
                sizes={CAROUSEL_IMAGE_SIZES}
                alt={image.alt ?? ''}
                loading={i === 0 ? 'eager' : 'lazy'}
                decoding="async"
                draggable={false}
                className={cn(
                  'size-full object-cover object-[50%_55%]',
                  // The push-in runs on the picture on show and holds on the
                  // one leaving, so it goes out as it was rather than snapping
                  // back mid-move. The rest reset unseen.
                  zoom && [
                    isActive || isLeaving ? 'scale-106' : 'scale-100',
                    isActive && 'transition-transform duration-(--duration-drift) ease-drift',
                    'motion-reduce:scale-100 motion-reduce:transition-none',
                  ],
                )}
              />
            </div>
          );
        })}
        {children && <div className="pointer-events-none absolute inset-0 z-10">{children}</div>}
        {progress === undefined ? (
          dots
        ) : (
          <div
            data-carousel-control
            className="absolute inset-x-0 bottom-4.5 z-20 flex justify-center"
          >
            <ProgressDots
              count={count}
              index={index}
              progress={progress}
              onSelect={go}
              labelFor={dotLabel}
            />
          </div>
        )}
      </section>
    );
  }

  if (variant === 'flat') {
    const chevron = cn(
      'grid size-11 shrink-0 place-items-center rounded-full text-on-panel',
      'transition-colors duration-(--duration-fast) ease-standard hover:text-brand',
      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-on-panel',
    );

    return (
      <section
        aria-roledescription="carousel"
        aria-label={label}
        className={cn('flex w-full touch-pan-y items-center gap-2 select-none', className)}
        aria-live={live}
        {...handlers}
      >
        {count > 1 && (
          <button
            type="button"
            data-carousel-control
            onClick={() => go(index - 1)}
            aria-label="Previous image"
            className={chevron}
          >
            <ChevronLeft aria-hidden="true" className="size-7" strokeWidth={1.5} />
          </button>
        )}

        <div className="relative aspect-card min-w-0 flex-1 overflow-hidden rounded-card bg-panel">
          {slides.map((image, i) => {
            const isActive = i === index;
            return (
              // A crossfade, not a slide: with one picture on show there is no
              // neighbour to travel towards, and a sideways move across a
              // photograph this size reads as the page scrolling.
              <figure
                key={`${image.src}-${i}`}
                aria-hidden={isActive ? undefined : true}
                className={cn(
                  'absolute inset-0 m-0 transition-opacity duration-(--duration-slow) ease-standard',
                  'motion-reduce:transition-none',
                  isActive ? 'opacity-100' : 'opacity-0',
                )}
              >
                <img
                  src={image.src}
                  srcSet={fileImageSrcSet(image.src)}
                  sizes={CAROUSEL_IMAGE_SIZES}
                  alt={image.alt ?? ''}
                  loading="lazy"
                  decoding="async"
                  draggable={false}
                  className="size-full object-cover"
                />
                {(image.eyebrow || image.caption) && (
                  // The scrim is the panel itself fading up from the foot, so
                  // the caption's `on-panel` is sitting on the ground it was
                  // paired with, whatever the photograph does behind it.
                  <figcaption className="absolute inset-x-0 bottom-0 bg-linear-to-t from-panel/85 to-transparent p-card pt-16 text-on-panel">
                    {image.eyebrow && (
                      <span className="block font-mono text-2xs text-brand uppercase">
                        {String(i + 1).padStart(2, '0')} · {image.eyebrow}
                      </span>
                    )}
                    {image.caption && (
                      <span className="mt-1.5 block text-lg font-semibold">{image.caption}</span>
                    )}
                  </figcaption>
                )}
              </figure>
            );
          })}
        </div>

        {count > 1 && (
          <button
            type="button"
            data-carousel-control
            onClick={() => go(index + 1)}
            aria-label="Next image"
            className={chevron}
          >
            <ChevronRight aria-hidden="true" className="size-7" strokeWidth={1.5} />
          </button>
        )}
      </section>
    );
  }

  return (
    <section
      aria-roledescription="carousel"
      aria-label={label}
      className={cn('relative aspect-[1/1.02] w-full touch-pan-y select-none', className)}
      aria-live={live}
      {...handlers}
    >
      {slides.map((image, i) => {
        const rel = offsetFrom(i, index, count);
        const isActive = rel === 0;
        // Only the front picture and its two neighbours are ever on screen. The
        // rest stay mounted at their ring position so they enter from the
        // correct side instead of appearing out of nowhere.
        const isFar = Math.abs(rel) > 1;

        return (
          <button
            type="button"
            key={`${image.src}-${i}`}
            // A real button, because clicking a neighbour brings it to the
            // front — that is an action, and a div with an onClick is one the
            // tab key walks straight past. The front picture is not a control,
            // so it stops being focusable while it is in front.
            tabIndex={isActive || isFar ? -1 : 0}
            aria-hidden={isActive ? undefined : true}
            aria-label={`Show ${image.alt || `image ${i + 1}`}`}
            onClick={() => go(i)}
            className={cn(
              'absolute top-0 left-1/2 ml-[-47%] h-full w-[94%] overflow-hidden rounded-card',
              'bg-panel p-0',
              // Only `transform` is transitioned, which is the design's own
              // choice and worth keeping: fading a blur across 820ms is what
              // makes a gallery look like it is struggling to load.
              'transition-transform duration-(--duration-slow) ease-standard',
              'motion-reduce:transition-none',
              'focus-visible:outline-2 focus-visible:outline-offset-2',
              'focus-visible:outline-focus-on-panel',
              isActive ? 'cursor-default shadow-on-panel' : 'cursor-pointer',
              isFar && 'pointer-events-none',
              !isFar && !isActive && 'pointer-events-auto',
            )}
            style={{
              transform: [
                `translateX(${rel * SPREAD * 100}%)`,
                `rotate(${isActive ? 0 : rel < 0 ? -TILT : TILT}deg)`,
                `scale(${isActive ? 1 : SCALE_X}, ${isActive ? 1 : SCALE_Y})`,
              ].join(' '),
              zIndex: 20 - Math.abs(rel),
              opacity: isFar ? 0 : isActive ? 1 : DIM,
              filter: isActive ? undefined : `blur(${BLUR}px)`,
            }}
          >
            <img
              src={image.src}
              srcSet={fileImageSrcSet(image.src)}
              sizes={CAROUSEL_IMAGE_SIZES}
              alt={image.alt ?? ''}
              loading="lazy"
              decoding="async"
              draggable={false}
              className="size-full object-cover"
            />
          </button>
        );
      })}

      {dots}
    </section>
  );
}
